/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from 'react';
import { DateTime, Interval } from 'luxon';
import { classNames } from 'primereact/utils';
import { Tooltip } from 'primereact/tooltip';
import { Job } from '../../types/processing-schemas.ts';
import { useTranslation } from 'react-i18next';
import { getLocaleTimeOffset, plurals } from 'src/utils/utils.ts';

interface Props {
  job: Job;
  property: 'created' | 'updated' | 'finished';
  id: number;
  showTooltip?: boolean;
}
export const DateRender: React.FC<Props> = ({ job, id, property, showTooltip }) => {
  const { t, i18n } = useTranslation();

  const [offsetMinute] = getLocaleTimeOffset();

  const dateIso = job[property];
  if (!dateIso) {
    return null;
  }

  let duration = undefined;
  if (job['finished']) {
    const created = DateTime.fromISO(job['created']);
    const finished = DateTime.fromISO(job['finished']);
    const interval = Interval.fromDateTimes(created, finished);
    duration =
      interval.length('seconds') <= 60
        ? `~ ${Math.ceil(interval.length('seconds'))} ${plurals(Math.ceil(interval.length('seconds')), 'sec')}.`
        : interval.length('minutes') <= 60
          ? `~ ${plurals(Math.ceil(interval.length('minutes')), 'min')}.`
          : ` ~ ${Math.ceil(interval.length('hours'))} h.`;
  }

  return (
    <div className={'flex align-items-center'}>
      <div>
        {DateTime.fromISO(dateIso)
          .plus({ minute: offsetMinute })
          .toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS, {
            locale: i18n.language ?? 'en'
          })}
      </div>
      {showTooltip && (
        <span>
          {duration && (
            <>
              <i className={classNames('pi pi-info-circle ml-2 cursor-auto', `${property}-${id}`)} />
              <Tooltip target={`.${property}-${id}`}>
                {job.updated && !job.finished && (
                  <div>
                    <span className="inline-block text-sm font-bold">{t(['date.updated_at', 'updated'])}:&nbsp;</span>
                    <span className="text-sm">
                      {DateTime.fromISO(job.updated)
                        .plus({ minute: offsetMinute })
                        .toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS, {
                          locale: i18n.language ?? 'en'
                        })}
                    </span>
                  </div>
                )}
                {job.finished && (
                  <div>
                    <span className="inline-block text-sm font-bold">{t(['date.finished_at', 'finished'])}:&nbsp;</span>
                    <span className="text-sm">
                      {DateTime.fromISO(job.finished)
                        .plus({ minute: offsetMinute })
                        .toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS, {
                          locale: i18n.language ?? 'en'
                        })}
                    </span>
                  </div>
                )}
                {duration && (
                  <div>
                    <span className="text-sm font-bold">{t(['misc.duration', 'duration'])}:&nbsp;</span>
                    <span className="text-sm font-semibold">{duration}</span>
                  </div>
                )}
              </Tooltip>
            </>
          )}
        </span>
      )}
    </div>
  );
};
